import { useEffect, useRef, useState, useCallback } from "react";
import { useGetFeatureFlag } from "lib/launch-darkly/feature-flags-provider";
import {
  getEnvVariable,
  VARIABLE_NAMES,
} from "lib/environment/environment-variables";

let sessionKeyPromise: Promise<string | null> | null = null;

function getSessionKey(): Promise<string | null> {
  if (sessionKeyPromise) return sessionKeyPromise;

  const clockworkHost = getEnvVariable(VARIABLE_NAMES.CLOCKWORK_HOST);
  sessionKeyPromise = fetch(`${clockworkHost}/usp/session-init`, {
    credentials: "include",
  })
    .then((res) => {
      if (!res.ok) {
        sessionKeyPromise = null;
        return null;
      }
      return res.json().then((json) => {
        const key = json.session_key?.split(".")[0] ?? null;
        if (!key) sessionKeyPromise = null;
        return key;
      });
    })
    .catch(() => {
      sessionKeyPromise = null;
      return null;
    });

  return sessionKeyPromise;
}

const USP_SDK_READY_TIMEOUT_MS = 5000;

function whenUspSdkReady(): Promise<Window["_Sardine"] | null> {
  if (window._Sardine) return Promise.resolve(window._Sardine);
  return new Promise((resolve) => {
    const script = document.querySelector<HTMLScriptElement>(
      'script[src*="/assets/loader.min.js"]'
    );
    if (!script) {
      resolve(null);
      return;
    }
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve(window._Sardine ?? null);
    };
    script.addEventListener("load", done, { once: true });
    script.addEventListener("error", done, { once: true });
    // Backstop for the case where the script already fired load/error before
    // we attached listeners (e.g. failed load), or if it never fires.
    setTimeout(done, USP_SDK_READY_TIMEOUT_MS);
  });
}

interface UseUspOptions {
  flowName: string;
}

export function useUsp({ flowName }: UseUspOptions) {
  const { getFeatureFlag } = useGetFeatureFlag();
  const contextRef = useRef<UspContext | null>(null);
  const pendingFlowRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  const clientId = getEnvVariable(VARIABLE_NAMES.USP_CLIENT_ID);
  const isEnabled = !!clientId && !!getFeatureFlag("ciam-sardine-enabled");

  const updateFlow = useCallback((newFlowName: string) => {
    if (!contextRef.current) {
      pendingFlowRef.current = newFlowName;
      return;
    }
    contextRef.current.updateConfig({ flow: newFlowName });
  }, []);

  useEffect(() => {
    if (!isEnabled) return;

    let cancelled = false;

    Promise.all([getSessionKey(), whenUspSdkReady()]).then(
      ([sessionKey, uspSdk]) => {
        if (cancelled || !sessionKey || !uspSdk) return;

        const uspHost = getEnvVariable(VARIABLE_NAMES.USP_HOST);
        const uspPixelHost = getEnvVariable(VARIABLE_NAMES.USP_PIXEL_HOST);
        const uspEnv = getEnvVariable(VARIABLE_NAMES.USP_ENV);
        const initialFlow = pendingFlowRef.current ?? flowName;

        contextRef.current = uspSdk.createContext({
          clientId,
          sessionKey,
          flow: initialFlow,
          environment: uspEnv,
          parentElement: document.body,
          apiSubdomain: uspHost,
          pixelSubdomain: uspPixelHost,
        });
        pendingFlowRef.current = null;

        setReady(true);
      }
    );

    return () => {
      cancelled = true;
      contextRef.current?.unbind();
      contextRef.current = null;
    };
    // flowName intentionally omitted: updateFlow handles changes without
    // tearing down the context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, clientId]);

  return { uspReady: ready, updateFlow };
}
